/**
 * Das eine Anzeigefenster für Dateien in Kontor — Angebote, Rechnungen,
 * Lieferscheine, Lieferantenbelege, Mailanhänge, Fotos, Editor-Vorschau.
 *
 * Vorher öffnete jeder Pfeil einen neuen Tab: man verlor die Seite aus dem
 * Blick, und die Rücknavigation lud alles neu. Eine einzige Lightbox hält den
 * Kontext und sieht überall gleich aus. Wer eine Datei zeigen will, nimmt
 * diese Komponente — keine zweite bauen.
 *
 * PDFs werden IMMER als Seitenbilder gezeigt, die der Server rendert
 * (`kontor_seitenbild`). Ein PDF im `<iframe>` hängt am PDF-Handler des
 * Browsers: Safari auf dem iPhone zeigt nur die erste Seite, Chrome auf
 * Android lädt die Datei herunter, Firefox unter Linux speichert sie je nach
 * Einstellung stumm. Ein `<img>` zeigt überall dasselbe (Benjamin, 24.09.2026:
 * „alle Dokumente an allen Stellen problemlos von unterwegs anschauen").
 *
 * Woher die Seiten kommen:
 *  - `bild`: die Datei ist ein Bild und wird direkt gezeigt.
 *  - `belegId`: Lieferantenbeleg, Seiten über `/belege/{id}/thumbnail`.
 *  - `seitenZahlUrl` + `seiteUrl`: alles andere mit fester Adresse
 *    (Angebot/Rechnung, Mailanhang).
 *  - `seitenBilder`: fertig gerenderte Seiten (data-URLs) für Dateien, die nur
 *    im Browser liegen — Editor-Vorschau, Lieferschein.
 *
 * Auf dem Telefon füllt das Fenster den Bildschirm; „Vergrößern" (oder
 * Doppeltippen auf eine Seite) zeigt die Seiten in doppelter Breite zum
 * Seitwärtsscrollen — für Kleingedrucktes, ohne die ganze App zu zoomen.
 */
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@vimaana/ui";

import { useBelegSeiten } from "../hooks/useBelege";
import { belegSeiteUrl } from "../lib/belege";
import { bildBreite } from "../lib/seitenbilder";
// .vg-btn steht in projekte.css — die Lightbox öffnet sich auch auf Seiten,
// die projekte.css sonst nicht laden (Rechnungsregister, Team).
import "../styles/projekte.css";
import "../styles/dokumentansicht.css";

/** `belegId` gesetzt = Beleg, wird als Seitenbild gezeigt statt als PDF. */
export type Dokument = {
  titel: string; unterzeile?: string;
  /** Die Datei selbst — für „Als PDF öffnen". */
  url: string;
  /** Adresse zum Herunterladen, falls sie sich von `url` unterscheidet. */
  download?: string;
  belegId?: string; bild?: boolean;
  /** Für Dateien mit fester Adresse, die kein Beleg sind: wo die Seitenzahl steht und wie eine Seite heißt. */
  seitenZahlUrl?: string; seiteUrl?: (seite: number, breite: number) => string;
  /** Schon gerenderte Seiten (data-URLs) — für Dateien ohne feste Adresse. */
  seitenBilder?: string[];
} | null;

export default function DokumentLightbox({ dok, onClose }: { dok: Dokument; onClose: () => void }) {
  const belegSeiten = useBelegSeiten(dok?.belegId);
  const fremdSeiten = useQuery<{ seiten: number }>({
    queryKey: ["lightbox-seiten", dok?.seitenZahlUrl],
    queryFn: () => apiGet<{ seiten: number }>(dok?.seitenZahlUrl as string),
    enabled: !!dok?.seitenZahlUrl && !dok?.bild,
    staleTime: 10 * 60_000,
  });
  const [fehler, setFehler] = useState(false);
  const [gross, setGross] = useState(false);
  // Zurücksetzen nur, wenn wirklich ein anderes Dokument kommt — Aufrufer
  // bauen `dok` und `onClose` oft bei jedem Rendern neu, und dann sprang die
  // Vergrößerung bei jeder Kleinigkeit zurück.
  const schluessel = dok ? `${dok.titel}|${dok.url}` : "";
  useEffect(() => {
    setFehler(false);
    setGross(false);
  }, [schluessel]);
  const offen = !!dok;
  useEffect(() => {
    if (!offen) return;
    // In der Einfangphase und mit stopPropagation: sonst schließt Escape
    // zugleich das Fenster darunter (Belegdetail, Nachrichten).
    const zu = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", zu, true);
    return () => window.removeEventListener("keydown", zu, true);
  }, [offen, onClose]);
  if (!dok) return null;

  const breite = bildBreite();
  let quellen: string[] | null = null;
  let laedt = false;
  if (dok.bild) {
    quellen = [dok.url];
  } else if (dok.seitenBilder) {
    quellen = dok.seitenBilder;
  } else if (dok.belegId) {
    laedt = belegSeiten.isLoading;
    const n = Math.max(1, belegSeiten.data?.seiten ?? 1);
    quellen = Array.from({ length: n }, (_, i) => belegSeiteUrl(dok.belegId as string, i, Math.min(breite, 1600)));
  } else if (dok.seitenZahlUrl && dok.seiteUrl) {
    laedt = fremdSeiten.isLoading;
    const n = Math.max(1, fremdSeiten.data?.seiten ?? 1);
    const seiteUrl = dok.seiteUrl;
    quellen = Array.from({ length: n }, (_, i) => seiteUrl(i, breite));
  }
  const seitenFehler = fremdSeiten.isError || belegSeiten.isError;

  return createPortal(
    /* Abgedunkelter Hintergrund und ein Panel mit Rand statt einer randlosen
       Vollbildfläche — sonst sieht die Vorschau aus wie ein Seitenwechsel, und
       wer zurückwill, schließt die App statt der Vorschau. Auf dem Telefon
       füllt das Panel den Bildschirm, der Kopf mit „Schließen" bleibt stehen. */
    <div className="vg-doklight" role="dialog" aria-modal="true" aria-label={dok.titel} onClick={onClose}>
      <div className={"vg-doklight-panel" + (quellen ? "" : " is-pdf")} onClick={(e) => e.stopPropagation()}>
        <div className="vg-doklight-kopf">
          <div className="vg-doklight-titel">
            <strong>{dok.titel}</strong>
            {dok.unterzeile && <span>{dok.unterzeile}</span>}
          </div>
          <span className="vg-doklight-aktionen">
            {quellen && !dok.bild && quellen.length > 1 && <span className="vg-doklight-seiten">{quellen.length} Seiten</span>}
            {quellen && (
              <button type="button" className="vg-btn" aria-pressed={gross} onClick={() => setGross((g) => !g)}>
                {gross ? "Einpassen" : "Vergrößern"}
              </button>
            )}
            <a className="vg-btn" href={dok.url} target="_blank" rel="noreferrer">
              {dok.bild ? "Original" : "PDF"}
            </a>
            {dok.download && <a className="vg-btn" href={dok.download} download>Laden</a>}
            <button type="button" className="vg-btn vg-doklight-zu" onClick={onClose}>Schließen</button>
          </span>
        </div>
        {quellen ? (
          <Seitenblatt
            quellen={quellen}
            titel={dok.titel}
            gross={gross}
            onGross={() => setGross((g) => !g)}
            foto={!!dok.bild}
            laedt={laedt}
            fehler={fehler || seitenFehler}
            onFehler={() => setFehler(true)}
            ersatz={<a href={dok.url} target="_blank" rel="noreferrer">Als PDF öffnen</a>}
          />
        ) : (
          <iframe title={dok.titel} src={`${dok.url}#toolbar=0&navpanes=0&statusbar=0&view=FitH`} />
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Die Blattfolge selbst — auch außerhalb der Lightbox benutzbar (die
 * PDF-Vorschau im Beleg-Editor hat ihren eigenen Kopf mit „Neu erzeugen").
 */
export function Seitenblatt({ quellen, titel, gross, onGross, foto = false, laedt = false, fehler = false, onFehler, ersatz }: {
  quellen: string[]; titel: string; gross: boolean; onGross?: () => void;
  foto?: boolean; laedt?: boolean; fehler?: boolean; onFehler?: () => void; ersatz?: ReactNode;
}) {
  if (laedt) return <div className="vg-doklight-blatt"><p className="vg-doklight-info">Vorschau wird geladen …</p></div>;
  if (fehler) {
    return (
      <div className="vg-doklight-blatt">
        <p className="vg-doklight-info">Die Vorschau konnte nicht erzeugt werden.{ersatz ? <> {ersatz}</> : null}</p>
      </div>
    );
  }
  return (
    <div className={"vg-doklight-blatt" + (gross ? " is-gross" : "")}>
      {quellen.map((src, i) => (
        <img
          key={src.length > 200 ? i : src}
          className={"vg-doklight-seite" + (foto ? " vg-doklight-foto" : "")}
          src={src}
          alt={quellen.length > 1 ? `${titel}, Seite ${i + 1} von ${quellen.length}` : titel}
          loading={i > 1 ? "lazy" : "eager"}
          decoding="async"
          onDoubleClick={onGross}
          onError={onFehler}
        />
      ))}
    </div>
  );
}
